<script setup lang="ts">
import { computed } from 'vue'
import { OWidget, OWidgetKPIValue, OWidgetSparkline } from '@ai/ui-components'
import type { ColdMonitoringKPI } from 'shared'
import {
  toWidgetDurationFromMinutes,
  toWidgetDurationFromSeconds,
  type WidgetDuration,
} from '@/utils/widget-duration'

const props = defineProps<{
  kpis?: ColdMonitoringKPI[]
  loading?: boolean
  error?: string | null
}>()

const emit = defineEmits<(e: 'expand', kpi: ColdMonitoringKPI) => void>()

const { t } = useI18n({ useScope: 'global' })

const sparklineVariant = (kpi: ColdMonitoringKPI): 'positive' | 'neutral' | 'negative' => {
  if (!kpi.sparkline || !kpi.prevValue) return 'neutral'
  if (kpi.value > kpi.prevValue) return 'positive'
  if (kpi.value < kpi.prevValue) return 'negative'
  return 'neutral'
}

const hasActivity = (value: number, series: number[]) => value !== 0 || series.some((v) => v !== 0)

const getDuration = (
  kpi: ColdMonitoringKPI,
  active: boolean,
): WidgetDuration | null | undefined => {
  if (kpi.id === 'avg-duration') return active ? toWidgetDurationFromSeconds(kpi.value) : null
  if (kpi.id === 'total-minutes') return active ? toWidgetDurationFromMinutes(kpi.value) : null
  return undefined
}

const getTitle = (kpi: ColdMonitoringKPI): string => {
  if (kpi.id === 'total-minutes') return t('cold_monitoring.kpi.total_calling_duration')
  return t(`cold_monitoring.kpi.${kpi.title}`)
}

const stats = computed(() =>
  (props.kpis ?? []).map((kpi) => {
    const series = (kpi.sparkline?.series ?? []).slice(-5)
    const active = hasActivity(kpi.value, series)
    const duration = getDuration(kpi, active)
    const displayValue = duration !== undefined ? undefined : active ? kpi.value : null

    return {
      raw: kpi,
      id: kpi.id,
      title: getTitle(kpi),
      sparkline: series,
      duration,
      displayValue,
    }
  }),
)
</script>

<template>
  <div class="@container">
    <div
      class="grid gap-4 grid-cols-6 @max-[1760px]:grid-cols-4 @max-[1580px]:grid-cols-3 @max-[880px]:grid-cols-2 @max-[580px]:grid-cols-1"
    >
      <OWidget
        v-for="kpi in stats"
        :key="kpi.id"
        :title="kpi.title"
        class="min-w-[280px]"
        icon="phone"
        @expand="emit('expand', kpi.raw)"
      >
        <template #kpi>
          <OWidgetKPIValue :value="kpi.displayValue" :duration="kpi.duration" />
        </template>
        <template #sparkline v-if="kpi.sparkline.length">
          <OWidgetSparkline :series="kpi.sparkline" :variant="sparklineVariant(kpi.raw)" />
        </template>
      </OWidget>
    </div>
  </div>
</template>
